import React, { FC, ReactNode, useCallback, useMemo } from 'react';
import { isMobile } from 'is-mobile';
import last from 'lodash/last';

import Range from '../../../Range';
import FilterButton from '../../../FilterButton';
import { ENERGIES, GEARBOX_TYPES, PRICES } from '../../../../services/core/sdk/constants/sdkFields';

import {
    IFilterCategory,
    IFilterRule,
    IGeoLocation,
    IPriceRange,
} from '../../../../redux/filters/filter.duck.interface';
import useTranslations from '@hooks/useTranslations';
import { BRAND, FILTER_CATEGORY } from '../../../../constants/main';
import { BrandTypes } from '../../../../types/brands';
import Icon, { Icons } from '../../../Icon';
import LocationInput from './LocationInput';
import { getFilterButtonId } from '../../utils';

interface GeneratedFilterButtonsProps {
    filters: IFilterRule[];
    availableFilters: IFilterRule[];
    filterCategories: IFilterCategory[];
    mobileHide: boolean;
    priceRange: IPriceRange;
    handleChange: (name: string, value: boolean | IPriceRange) => void;
    onGeoLocationChange: (geoLocation: IGeoLocation, distanceRadius: number) => void;
    renderFinanceToggle: ReactNode;
    disabled?: boolean;
}

const GeneratedFilterButtons: FC<GeneratedFilterButtonsProps> = ({
    filters,
    filterCategories,
    availableFilters,
    mobileHide,
    priceRange,
    handleChange,
    onGeoLocationChange,
    renderFinanceToggle,
    disabled,
}): JSX.Element => {
    const { t } = useTranslations();

    const filterButtons = useMemo(
        () =>
            filters.map((filter) => ({
                ...filter,
                disabled: availableFilters.find((filteredFilter) => filter.name === filteredFilter.name) === undefined,
            })),
        [filters, availableFilters]
    );

    const sortedFilterCategories: IFilterCategory[] = [
        filterCategories.find((category) => category.name === PRICES),
        filterCategories.find((category) => category.name === ENERGIES),
        filterCategories.find((category) => category.name === GEARBOX_TYPES),
    ];

    /**
     * TODO: REFACTOR, duplication of Configurable/GeneratedFilterButtons::getCategoryDisplayNameTranslated
     */
    const getCategoryDisplayNameTranslated = useCallback((displayName: string): string => {
        switch (displayName) {
            case FILTER_CATEGORY.BUDGET:
                return t('filters.category.budget');
            case FILTER_CATEGORY.FUEL:
                return t('filters.category.fuel');
            case FILTER_CATEGORY.GEARBOX:
            case FILTER_CATEGORY.GEARBOX_TYPES:
                return t('filters.category.gearbox');
            case FILTER_CATEGORY.ENERGIES:
                return t('filters.category.fuel');
            case FILTER_CATEGORY.PAYMENT_JOURNEY_TYPE:
                return t('paymentJourneyType');
            case FILTER_CATEGORY.PRICES:
                return t('filters.category.prices');
            default:
                return displayName;
        }
    }, []);

    const Buttons = sortedFilterCategories.map((category) => {
        if (filterButtons.find((filterButton) => filterButton.parent === category?.name)) {
            return (
                <div
                    key={category?.name}
                    className={`filter-buttons-wrapper ${mobileHide ? 'onMobile-hidden' : ''} ${
                        category.name === FILTER_CATEGORY.PRICES ? 'filter-buttons-wrapper--price-range' : ''
                    }`}
                    id={category?.name}
                    data-testid={`TESTING_FILTER_${category?.name}`}
                >
                    {category.name !== FILTER_CATEGORY.PRICES ? (
                        <h3 className="filter-title">{getCategoryDisplayNameTranslated(category.name)}</h3>
                    ) : (
                        <div className="show-if-mobile mobile-filter-title">
                            {/*TODO-TRANSLATION-FIX*/}
                            <h3 className="filter-title">{t('filters.category.budget')}</h3>
                        </div>
                    )}
                    {filterButtons.map((filter, index) => {
                        if (filter.parent === category.name) {
                            if (filter.parent === PRICES && priceRange) {
                                return (
                                    <div
                                        key={`${category?.name}-${filter.name}`}
                                        className="price-geoLocation-container"
                                    >
                                        <div className="price-mobile-finance-range">
                                            <Range
                                                className="slider-over"
                                                key={filter.name}
                                                min={priceRange.min}
                                                max={priceRange.max}
                                                availableMin={priceRange.availableMin}
                                                availableMax={priceRange.availableMax}
                                                value={
                                                    filter.value
                                                        ? [
                                                              (filter?.value as IPriceRange)?.min,
                                                              (filter?.value as IPriceRange)?.max,
                                                          ]
                                                        : [priceRange.availableMin, priceRange.availableMax]
                                                }
                                                onAfterChange={(value: Array<number>) =>
                                                    handleChange(filter.name, { min: value[0], max: value[1] })
                                                }
                                            />
                                        </div>
                                        <div className="geolocation-input-container">
                                            <LocationInput onChange={onGeoLocationChange} />
                                        </div>
                                    </div>
                                );
                            } else if (typeof filter.value === 'boolean') {
                                return (
                                    <FilterButton
                                        className="stock-generated-filter-button"
                                        key={`${category?.name}-${filter.name}-${index}`}
                                        id={getFilterButtonId(filter.name)}
                                        active={filter.value}
                                        disabled={disabled || (filter.value ? false : filter.disabled)}
                                        onClick={() => {
                                            handleChange(filter.name, !filter.value);
                                        }}
                                        isMobile={isMobile()}
                                    >
                                        <div className="disp-inline">
                                            {last(filter.displayName.split('.'))}
                                            {[BrandTypes.DS, BrandTypes.AP].includes(BRAND as BrandTypes) &&
                                                filter.value && <Icon className="info-check" name={Icons.Check} />}
                                        </div>
                                    </FilterButton>
                                );
                            }
                        }
                    })}
                </div>
            );
        }
    });

    return <>{Buttons}</>;
};

export default GeneratedFilterButtons;
